'use client';

import { createContext, useContext, useMemo, ReactNode } from 'react';
import { HOMEPAGE_V2_FLAG, VisitorType } from 'utils/bootstrapHelpers';

export type HomepageVariant = 'control' | 'v2';
export type InvestorExperience = 'accredited' | 'qp' | 'retail';

interface EventProperties {
  [key: string]: unknown;
  homepage_variant: HomepageVariant;
  experiment: string;
  visitor_type: VisitorType;
  investor_experience: InvestorExperience;
}

interface HomepageABTestContextValue {
  variant: HomepageVariant;
  isV2: boolean;
  visitorType: VisitorType;
  investorExperience: InvestorExperience;
  getEventProperties: () => EventProperties;
}

const HomepageABTestContext = createContext<HomepageABTestContextValue | null>(null);

interface HomepageABTestProviderProps {
  children: ReactNode;
  variant: HomepageVariant;
  visitorType?: VisitorType;
  investorExperience?: InvestorExperience;
  injectExperience?: boolean;
}

export const HomepageABTestProvider = ({
  children,
  variant,
  visitorType = 'returning',
  investorExperience = 'accredited',
  injectExperience: _injectExperience = false,
}: HomepageABTestProviderProps) => {
  const value = useMemo(
    () => ({
      variant,
      isV2: variant === 'v2',
      visitorType,
      investorExperience,
      getEventProperties: () => ({
        homepage_variant: variant,
        experiment: HOMEPAGE_V2_FLAG,
        visitor_type: visitorType,
        investor_experience: investorExperience,
      }),
    }),
    [variant, visitorType, investorExperience],
  );

  return <HomepageABTestContext.Provider value={value}>{children}</HomepageABTestContext.Provider>;
};

export const useHomepageABTestContext = (): HomepageABTestContextValue => {
  const context = useContext(HomepageABTestContext);

  if (!context) {
    return {
      variant: 'control',
      isV2: false,
      visitorType: 'returning',
      investorExperience: 'accredited' as InvestorExperience,
      getEventProperties: () => ({
        homepage_variant: 'control' as HomepageVariant,
        experiment: HOMEPAGE_V2_FLAG,
        visitor_type: 'returning' as VisitorType,
        investor_experience: 'accredited' as InvestorExperience,
      }),
    };
  }

  return context;
};

export default HomepageABTestContext;
